import Parse from 'parse';

Parse.initialize(
  process.env.REACT_APP_APP_ID,
  process.env.REACT_APP_JAVASCRIPT_KEY,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

// Cache for operator data to avoid duplicate API calls
let cachedOperatorData = null;
let cacheTimestamp = null;
const CACHE_DURATION = 30000; // 30 seconds cache
let fetchPromise = null; // Promise lock to prevent concurrent fetches

// Clear only the SESSION keys on logout / session-expiry. Deliberately does NOT
// touch rememberedUsername / rememberedPassword so "Remember me" survives logout
// (those are cleared only when the user logs in with the box unchecked).
const clearSessionStorage = () => {
  localStorage.removeItem('operatorId');
  localStorage.removeItem('operatorName');
  localStorage.removeItem('operatorEmail');
  localStorage.removeItem('operatorLogo');
  localStorage.removeItem('sessionToken');
};

export const authProvider = {
  login: async (params) => {
    const { username, password } = params;

    try {
      if (!username || !password) {
        throw new Error('Username and password are required.');
      }

      const response = await Parse.Cloud.run('operatorLogin', {
        username,
        password
      });

      // Cache operator data
      cachedOperatorData = response.operator;
      cacheTimestamp = Date.now();

      return {
        operatorId: response.operator.objectId,
        operatorName: response.operator.name,
        operatorEmail: response.operator.email,
        sessionToken: response.operator.sessionToken
      };
    } catch (error) {
      return Promise.reject(error);
    }
  },

  logout: async () => {
    const operatorId = localStorage.getItem('operatorId');

    // Best-effort server logout — never let a failure here block the local
    // logout + redirect (that was the cause of the "click twice to logout" bug).
    if (operatorId) {
      try {
        await Parse.Cloud.run('operatorLogout', { operatorId });
      } catch (cloudError) {
        console.error('Cloud logout error:', cloudError);
      }
    }

    // Clear cache
    cachedOperatorData = null;
    cacheTimestamp = null;
    fetchPromise = null;

    // Clear stored auth data. Keeps rememberedUsername / rememberedPassword so
    // "Remember me" survives logout (see clearSessionStorage).
    clearSessionStorage();

    // Logout from Parse — ignore errors (e.g. an already-invalid session) so the
    // redirect below always runs on the first click.
    try {
      await Parse.User.logOut();
    } catch (parseError) {
      console.error('Parse logout error:', parseError);
    }

    window.location.replace('/login');
    return Promise.resolve();
  },

  checkAuth: async () => {
    const operatorId = localStorage.getItem('operatorId');
    const sessionToken = localStorage.getItem('sessionToken');

    if (operatorId && sessionToken) {
      try {
        // Check if we have valid cached data
        const now = Date.now();
        if (cachedOperatorData && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
          return Promise.resolve();
        }

        // If a fetch is already in progress, wait for it
        if (fetchPromise) {
          await fetchPromise;
          return Promise.resolve();
        }

        // Create fetch promise and store it
        fetchPromise = (async () => {
          try {
            // Direct REST API call to verify session
            const response = await fetch(
              `${process.env.REACT_APP_SERVER_URL}/classes/_User/${operatorId}`,
              {
                method: 'GET',
                headers: {
                  'X-Parse-Application-Id': process.env.REACT_APP_APP_ID,
                  'X-Parse-Session-Token': sessionToken,
                  'Content-Type': 'application/json'
                }
              }
            );

            if (!response.ok) {
              const errorData = await response.json();

              // Check for invalid session token (code 209)
              if (errorData.code === 209 || errorData.error?.includes('Invalid session token')) {
                cachedOperatorData = null;
                cacheTimestamp = null;
                fetchPromise = null;
                clearSessionStorage();
                await Parse.User.logOut();
                throw new Error('Session expired');
              }

              throw new Error(errorData.error || 'Failed to verify session');
            }

            const userData = await response.json();

            // Cache the operator data
            cachedOperatorData = userData;
            cacheTimestamp = Date.now();
          } finally {
            // Clear the fetch promise
            fetchPromise = null;
          }
        })();

        await fetchPromise;
        return Promise.resolve();
      } catch (error) {
        // If error code is 209 (Invalid session token), logout
        if (error.code === 209 || error.message?.includes('Invalid session token')) {
          cachedOperatorData = null;
          cacheTimestamp = null;
          fetchPromise = null;
          clearSessionStorage();
          await Parse.User.logOut();
          return Promise.reject(new Error('Session expired'));
        }

        // For other errors, still allow if operator exists locally
        return Promise.resolve();
      }
    }
    return Promise.reject();
  },

  checkError: async (error) => {
    const status = error.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem('operatorId');
      localStorage.removeItem('sessionToken');
      return Promise.reject();
    }
    return Promise.resolve();
  },

  getPermissions: async () => {
    const operatorId = localStorage.getItem('operatorId');
    return operatorId ? Promise.resolve('Operator') : Promise.reject();
  },

  getIdentity: async () => {
    const operatorId = localStorage.getItem('operatorId');
    if (operatorId) {
      // Use cached data if available
      if (cachedOperatorData) {
        return Promise.resolve({
          id: cachedOperatorData.objectId || operatorId,
          fullName: cachedOperatorData.name || localStorage.getItem('operatorName'),
          email: cachedOperatorData.email || localStorage.getItem('operatorEmail'),
          avatar: cachedOperatorData.operatorLogo || localStorage.getItem('operatorLogo'),
          role: 'Operator'
        });
      }

      // Fallback to localStorage data (no API call)
      return Promise.resolve({
        id: operatorId,
        fullName: localStorage.getItem('operatorName'),
        email: localStorage.getItem('operatorEmail'),
        avatar: localStorage.getItem('operatorLogo'),
        role: 'Operator'
      });
    }
    return Promise.reject();
  }
};
